import { useCallback, useMemo, useState } from "react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  AlertVideo,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  FeatureIntroducedDocument,
  useFeatureIntroducedQuery,
  useIntroduceFeatureMutation,
} from "@/graphql";

export function useFeatureAlert({
  featureType,
  title,
  description,
  dialog = false,
  video = null,
  className,
}: {
  featureType: string;
  title: string;
  description: string;
  dialog?: boolean;
  video?: { vimeoId: string; title: string } | null;
  className?: string;
}) {
  const { data } = useFeatureIntroducedQuery({
    variables: { featureType },
  });
  const [introduceFeature] = useIntroduceFeatureMutation({
    refetchQueries: [
      {
        query: FeatureIntroducedDocument,
        variables: { featureType },
      },
    ],
  });
  const [areInstructionsOpen, setAreInstructionsOpen] = useState(false);
  const handleOpen = useCallback(() => {
    setAreInstructionsOpen(true);
  }, [setAreInstructionsOpen]);
  const handleConfirm = useCallback(() => {
    introduceFeature({ variables: { featureType } });
    setAreInstructionsOpen(false);
  }, [featureType, introduceFeature, setAreInstructionsOpen]);
  const result = useMemo(() => {
    return {
      alertNode:
        data &&
        !data.featureIntroduced &&
        (dialog ? (
          <AlertDialog
            open={areInstructionsOpen}
            onOpenChange={setAreInstructionsOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{title}</AlertDialogTitle>
                <AlertDialogDescription>{description}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Remind me later</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm}>
                  Got it
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Alert className={className}>
            {video && (
              <AlertVideo>
                <iframe
                  src={`https://player.vimeo.com/video/${video.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479&muted=1&autoplay=1`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen={true}
                  className="absolute top-0 left-0 h-full w-full rounded-lg border-0 bg-blue-200"
                  title={video.title}
                />
                <script src="https://player.vimeo.com/api/player.js" />
              </AlertVideo>
            )}
            <AlertTitle>{title}</AlertTitle>
            <AlertDescription className="gap-6">
              {description}
              <Button
                inverse
                size="sm"
                variant="glass"
                intent="secondary"
                className="w-24"
                onClick={handleConfirm}
              >
                Got it!
              </Button>
            </AlertDescription>
          </Alert>
        )),
      onOpenAlert: handleOpen,
    };
  }, [
    data?.featureIntroduced,
    areInstructionsOpen,
    setAreInstructionsOpen,
    description,
    handleConfirm,
    handleOpen,
    title,
    className,
  ]);
  return result;
}
